/**
 * Detection helpers for PWA install prompts.
 *
 * Apple does NOT expose the `beforeinstallprompt` event that Chromium does,
 * so iOS Safari users must be guided through the manual Share → Add to
 * Home Screen flow. These helpers identify when to show that guidance.
 */

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS-specific
  // @ts-expect-error — non-standard, iOS Safari only
  if (window.navigator.standalone === true) return true;
  // Standards-based
  return window.matchMedia('(display-mode: standalone)').matches;
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  // Exclude Chrome/Firefox/Edge on iOS — they all sub-classify themselves.
  const notOtherBrowser = !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua);
  return isIos && notOtherBrowser;
}

export function isMacSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Touch-capable Macs (iPadOS-on-Mac) are caught by isIosSafari already.
  return /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
}

/** macOS 14 (Sonoma) ships the "Add to Dock" feature. UA only tells us
 *  major Mac OS version, so we approximate with Safari 17+. */
export function supportsMacAddToDock(): boolean {
  if (!isMacSafari()) return false;
  const m = navigator.userAgent.match(/Version\/(\d+)/);
  return !!m && parseInt(m[1], 10) >= 17;
}

/** True if we should NUDGE the user to install (iOS Safari, not already
 *  installed, hasn't dismissed before, has been on the site long enough). */
export function shouldShowIosInstallPrompt(): boolean {
  if (!isIosSafari()) return false;
  if (isStandalone()) return false;
  try {
    if (localStorage.getItem('dd_a2hs_dismissed') === '1') return false;
  } catch { /* SSR / private mode */ }
  return true;
}

export function dismissInstallPrompt(): void {
  try { localStorage.setItem('dd_a2hs_dismissed', '1'); } catch { /* ignore */ }
}
